import { Button, Field as ChakraField, Fieldset, Grid, GridItem, Input, Text } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { useAppSelector } from '@hooks/redux.hooks';
import { RoleSelectors } from '@redux/authent/authent.selectors';
import { getRolesByCategory, isBasicRole } from '@utils/authent/roles.utils';
import { Field, FormikProvider } from 'formik';
import { FormattedMessage } from 'react-intl';

import { RoleEnum, RoleOperationalDomainCategoryEnum, RoleTaskCategoryEnum } from '@/types/authent/roles.types';

import { useProfileFormContext } from './ProfileFormContainer';

type CategoryGridProps<T> = {
  categories: T[];
  labelKey: string;
};

function CategoryGrid<T extends RoleTaskCategoryEnum | RoleOperationalDomainCategoryEnum>({
  categories,
  labelKey,
}: Readonly<CategoryGridProps<T>>) {
  const roles = useAppSelector(RoleSelectors.selectAllRoles);
  const { values, setFieldValue } = useProfileFormContext();

  const handleClick = (categoryRoles: RoleEnum[], isActive: boolean) => {
    if (isActive) {
      void setFieldValue(
        'roles',
        values.roles.filter((role) => isBasicRole(role) || !categoryRoles.includes(role)),
      );
    } else {
      void setFieldValue('roles', [...new Set([...values.roles, ...categoryRoles])]);
    }
  };

  return (
    <Grid width="100%" gapY={2} gapX={1} templateColumns="repeat(2,1fr)">
      <GridItem colSpan={2}>
        <Text color="neutral.300">
          <FormattedMessage id={`${labelKey}.title`} />
        </Text>
      </GridItem>
      {categories.map((category) => {
        const categoryRoles = getRolesByCategory(roles, category);
        const isActive = categoryRoles.every((role) => values.roles.includes(role));
        const isHalfActive = !isActive && categoryRoles.some((role) => values.roles.includes(role));
        return (
          <GridItem key={category}>
            <Button
              variant="toggle"
              position="relative"
              size="toggleM"
              disabled={categoryRoles.length === 0}
              data-active={isActive}
              data-half-active={isHalfActive}
              onClick={() => handleClick(categoryRoles, isActive)}
            >
              <FormattedMessage id={`${labelKey}.${category}`} />
            </Button>
          </GridItem>
        );
      })}
    </Grid>
  );
}

export default function ProfileForm() {
  const formik = useProfileFormContext();
  return (
    <CockpitFormLayout title="components.usersManagement.profiles.settings.title">
      <FormikProvider value={formik}>
        <Fieldset.Root
          height="min-content"
          invalid={Object.keys(formik.errors).some((key) => formik.touched[key as keyof typeof formik.touched])}
        >
          <ChakraField.Root required variant="cockpit" invalid={formik.touched.name && !!formik.errors.name}>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.usersManagement.profiles.settings.name" />
              <RequiredIndicator />
              {formik.initialValues.name !== formik.values.name && <ModifiedTag />}
            </ChakraField.Label>
            <Field as={Input} type="text" name="name" id="name" />
          </ChakraField.Root>
        </Fieldset.Root>
      </FormikProvider>
      <CategoryGrid
        labelKey="components.usersManagement.profiles.settings.mission"
        categories={Object.values(RoleTaskCategoryEnum).filter(
          (category) =>
            category !== RoleTaskCategoryEnum.SUPER_ADMINISTRATION && category !== RoleTaskCategoryEnum.APPLICATION,
        )}
      />
      <CategoryGrid
        labelKey="components.usersManagement.profiles.settings.specialization"
        categories={Object.values(RoleOperationalDomainCategoryEnum)}
      />
    </CockpitFormLayout>
  );
}
